import React, { useState } from 'react';
import { X, User, Phone, Mail, MapPin, Building } from 'lucide-react';
import { baserowAPI, Customer, TABLES } from '../services/baserowAPI';
import { invalidateClientsCache } from '../hooks/useClients';

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientCreated?: (client: Customer) => void;
}

export const AddClientModal: React.FC<AddClientModalProps> = ({
  isOpen,
  onClose,
  onClientCreated,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    billingAddress: '',
    interlocutorName: '',
    interlocutorPhone: '',
    interlocutorMail: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Le nom du client est obligatoire');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Créer le client avec les champs Baserow appropriés
      const newClient = await baserowAPI.createRow<Customer>(TABLES.CUSTOMERS, {
        field_7245: formData.name.trim(), // Name
        field_7254: formData.type.trim() || null, // Type
        field_7255: formData.billingAddress.trim() || null, // Billing Address
        field_7256: formData.interlocutorName.trim() || null, // Interlocutor Name
        field_7257: formData.interlocutorPhone.trim() || null, // Interlocutor Phone
        field_7258: formData.interlocutorMail.trim() || null, // Interlocutor Mail
      });

      console.log('Client créé avec succès:', newClient);
      
      // Réinitialiser le cache des clients
      invalidateClientsCache();
      
      // Réinitialiser le formulaire
      setFormData({
        name: '',
        type: '',
        billingAddress: '',
        interlocutorName: '',
        interlocutorPhone: '',
        interlocutorMail: '',
      });

      // Notifier le parent
      if (onClientCreated) {
        onClientCreated(newClient);
      }

      // Fermer la modal
      onClose();
    } catch (err) {
      console.error('Erreur lors de la création du client:', err);
      setError('Erreur lors de la création du client. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <User className="w-6 h-6 text-blue-600 mr-2" />
            <h2 className="text-xl font-semibold">Créer un nouveau client</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Nom du client (obligatoire) */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Nom du client *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Ex: Entreprise ABC, M. Dupont..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Type de client */}
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                Type de client
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  placeholder="Ex: Particulier, Entreprise, Association..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Adresse de facturation */}
            <div>
              <label htmlFor="billingAddress" className="block text-sm font-medium text-gray-700 mb-2">
                Adresse de facturation
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <textarea
                  id="billingAddress"
                  name="billingAddress"
                  value={formData.billingAddress}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Adresse complète de facturation..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            {/* Interlocuteur */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Interlocuteur</h3>
              
              <div className="space-y-4">
                {/* Nom interlocuteur */}
                <div>
                  <label htmlFor="interlocutorName" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'interlocuteur
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      id="interlocutorName"
                      name="interlocutorName"
                      value={formData.interlocutorName}
                      onChange={handleChange}
                      placeholder="Nom du contact principal..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Téléphone interlocuteur */}
                <div>
                  <label htmlFor="interlocutorPhone" className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone de l'interlocuteur
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="tel"
                      id="interlocutorPhone"
                      name="interlocutorPhone"
                      value={formData.interlocutorPhone}
                      onChange={handleChange}
                      placeholder="Ex: 01 23 45 67 89"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Email interlocuteur */}
                <div>
                  <label htmlFor="interlocutorMail" className="block text-sm font-medium text-gray-700 mb-2">
                    Email de l'interlocuteur
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      id="interlocutorMail"
                      name="interlocutorMail"
                      value={formData.interlocutorMail}
                      onChange={handleChange}
                      placeholder="contact@exemple.fr"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Création...' : 'Créer le client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
